import { useTranslation } from 'react-i18next';
import {
  AlertErrorRedIcon,
  AlertYellowIcon,
  CheckboxCircleGreenIcon,
  CloseIcon,
  InfoBlueIcon,
} from '@/assets/icons';

interface CustomToastProps {
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
  closeToast?: () => void;
  actionButton?: {
    label: string;
    onClick: () => void;
  };
}

const CustomToast = ({ message, type, closeToast, actionButton }: CustomToastProps) => {
  const { t } = useTranslation();
  // Get the appropriate icon and background color based on type
  const getIconAndBackground = () => {
    switch (type) {
      case 'success':
        return {
          icon: <img src={CheckboxCircleGreenIcon} alt={t('success')} className="w-5 h-5" />,
          bgColor: 'bg-[#DCFCE7]', // Light green background
        };
      case 'error':
        return {
          icon: <img src={AlertErrorRedIcon} alt={t('error')} className="w-5 h-5" />,
          bgColor: 'bg-[#FEE2E2]', // Light red background
        };
      case 'info':
        return {
          icon: <img src={InfoBlueIcon} alt={t('info')} className="w-5 h-5" />,
          bgColor: 'bg-[#DBEAFE]', // Light blue background
        };
      case 'warning':
        return {
          icon: <img src={AlertYellowIcon} alt={t('warning')} className="w-5 h-5" />,
          bgColor: 'bg-[#FEF3C7]', // Light yellow background
        };
      default:
        return {
          icon: <img src={InfoBlueIcon} alt={t('info')} className="w-5 h-5" />,
          bgColor: 'bg-[#DBEAFE]',
        };
    }
  };

  const { icon, bgColor } = getIconAndBackground();

  const handleClose = () => {
    if (closeToast) {
      closeToast();
    }
  };

  return (
    <div
      className="flex items-center gap-3 bg-white rounded-ds-default px-4 py-3 min-h-[52px] w-auto mx-auto whitespace-nowrap"
      style={{ boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)' }}
    >
      {/* Icon with colored background */}
      <div className={`flex-shrink-0 ${bgColor} rounded-full p-1`}>{icon}</div>

      {/* Message */}
      <p className="flex-1 text-[#111827]">{message}</p>

      {/* Optional Action Button */}
      {actionButton && (
        <button
          className="flex-shrink-0 px-3 py-1.5 text-sm font-medium text-primary hover:text-primary/80 hover:bg-primary/10 rounded transition-colors"
          onClick={() => {
            console.log('🔵 CustomToast: Action button clicked');
            console.log('🔵 CustomToast: actionButton:', actionButton);
            console.log('🔵 CustomToast: actionButton.onClick type:', typeof actionButton.onClick);
            try {
              actionButton.onClick();
              console.log('🔵 CustomToast: onClick executed successfully');
            } catch (error) {
              console.error('🔴 CustomToast: Error executing onClick:', error);
            }
            handleClose();
          }}
        >
          {actionButton.label}
        </button>
      )}

      {/* Close button */}
      <button
        className="flex-shrink-0 text-ds-text-tertiary hover:text-ds-text-secondary transition-colors"
        onClick={handleClose}
        aria-label={t('closeNotification')}
      >
        <img src={CloseIcon} alt={t('close')} className="w-5 h-5" />
      </button>
    </div>
  );
};

export default CustomToast;
